// src/pages/jobs/JobsPage.jsx
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import useJobs from "../../hooks/useJobs";
import JobCard from "../../components/jobs/JobCard";
import Button from "../../components/ui/Button";
import AppLayout from "../../components/layout/AppLayout";
import styles from "./JobsPage.module.css";

// ── Debounce helper ──────────────────────────────────────────
function useDebounce(callback, delay) {
  const timeoutRef = useRef(null);
  const debouncedFn = useCallback((...args) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => callback(...args), delay);
  }, [callback, delay]);

  useEffect(() => () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); }, []);
  return debouncedFn;
}

// ── Province → city/keyword aliases (lowercase, partial match) ────────────────
const PROVINCE_KEYWORDS = {
  "Gauteng":       ["gauteng","johannesburg","joburg","jo'burg","pretoria","tshwane","sandton","midrand","centurion","ekurhuleni","soweto","randburg","roodepoort","germiston","edenvale","alberton","benoni","boksburg"],
  "Western Cape":  ["western cape","cape town","cape","stellenbosch","george","paarl","worcester","bellville","tyger valley","tygervalley","franschhoek","hermanus","knysna","mossel bay"],
  "KwaZulu-Natal": ["kwazulu","kwazulu-natal","kzn","durban","pietermaritzburg","pmb","umhlanga","newcastle","richards bay","empangeni","pinetown"],
  "Eastern Cape":  ["eastern cape","port elizabeth","gqeberha","east london","mthatha","uitenhage","king william's town","queenstown"],
  "Limpopo":       ["limpopo","polokwane","tzaneen","thohoyandou","mokopane","phalaborwa"],
  "Mpumalanga":    ["mpumalanga","nelspruit","mbombela","witbank","emalahleni","middelburg","secunda"],
  "North West":    ["north west","rustenburg","mahikeng","mafikeng","klerksdorp","potchefstroom"],
  "Free State":    ["free state","bloemfontein","welkom","phuthaditjhaba","bethlehem","kroonstad"],
  "Northern Cape": ["northern cape","kimberley","upington","springbok","de aar"],
};

// Always pass these through regardless of province selection
const REMOTE_RE = /\b(remote|worldwide|global|south africa|za)\b/i;

function filterByProvinces(jobs, provinces) {
  if (!provinces || provinces.length === 0) return jobs;
  const kwSets = provinces.map(p => PROVINCE_KEYWORDS[p] || [p.toLowerCase()]);
  return jobs.filter(job => {
    const loc = (job.location || "").toLowerCase();
    if (REMOTE_RE.test(loc)) return true;
    return kwSets.some(kws => kws.some(kw => loc.includes(kw)));
  });
}

// ── Constants ────────────────────────────────────────────────
const TYPE_MODES = [
  { id: "all",       label: "All Jobs"  },
  { id: "freelance", label: "Freelance" },
  { id: "permanent", label: "Permanent" },
];

const CATEGORIES = [
  { id: "tech",      label: "Tech & Dev",    keyword: "developer software engineer" },
  { id: "design",    label: "Design",        keyword: "graphic designer UI UX"      },
  { id: "marketing", label: "Marketing",     keyword: "marketing SEO social media"  },
  { id: "finance",   label: "Finance",       keyword: "accountant bookkeeper finance" },
  { id: "admin",     label: "Admin & Ops",   keyword: "virtual assistant admin"     },
  { id: "sales",     label: "Sales",         keyword: "sales representative business development" },
  { id: "creative",  label: "Creative",      keyword: "photography videography content" },
  { id: "trades",    label: "Trades",        keyword: "plumber electrician carpenter" },
  { id: "data",      label: "Data",          keyword: "data analyst Power BI SQL"   },
  { id: "education", label: "Education",     keyword: "tutor teacher training"      },
];

// ── Category icons via Lucide (single consistent set) ────────
import {
  Code2, Palette, TrendingUp, DollarSign, Monitor,
  Target, Aperture, Wrench, BarChart2, GraduationCap,
  Search as SearchIcon, MapPin as MapPinIcon, Loader2,
} from "lucide-react";

const CATEGORY_ICONS = {
  tech:      Code2,
  design:    Palette,
  marketing: TrendingUp,
  finance:   DollarSign,
  admin:     Monitor,
  sales:     Target,
  creative:  Aperture,
  trades:    Wrench,
  data:      BarChart2,
  education: GraduationCap,
};

function CategoryIcon({ id, size = 22 }) {
  const Icon = CATEGORY_ICONS[id];
  return Icon ? <Icon size={size} strokeWidth={2} /> : null;
}

// ── Skeleton loading animation ───────────────────────────────
function JobSearchLoader() {
  return (
    <div className={styles.loaderWrap}>
      <div className={styles.loaderBeam} />
      <div className={styles.loaderStatus}>
        <Loader2 className={styles.loaderSpinner} size={20} strokeWidth={2} color="var(--color-accent)" />
        <span className={styles.loaderText}>
          Searching across all sources
          <span className={styles.dots}><span>.</span><span>.</span><span>.</span></span>
        </span>
      </div>
      <div className={styles.skeletonGrid}>
        {[...Array(6)].map((_, i) => (
          <div key={i} className={styles.skeletonCard} style={{ animationDelay: `${i * 0.06}s` }}>
            <div className={styles.skRow}>
              <div className={styles.skBlock} style={{ width: "60%", height: 16 }} />
              <div className={styles.skPill} />
            </div>
            <div className={styles.skBlock} style={{ width: "40%", height: 12, marginTop: 10 }} />
            <div className={styles.skBlock} style={{ width: "35%", height: 11, marginTop: 6 }} />
            <div className={styles.skSalary} />
            <div className={styles.skBlock} style={{ width: "100%", height: 11, marginTop: 14 }} />
            <div className={styles.skBlock} style={{ width: "85%", height: 11, marginTop: 5 }} />
            <div className={styles.skRow} style={{ marginTop: 16 }}>
              <div className={styles.skBlock} style={{ width: 48, height: 11 }} />
              <div className={styles.skBtn} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function JobsPage() {
  const { userProfile, savedJobIds } = useAuth();
  const navigate        = useNavigate();
  const [searchParams]  = useSearchParams();
  const { jobs: rawJobs, loading, error, search, loadMore, hasMore } = useJobs();

  const [query,          setQuery]          = useState(searchParams.get("q") || "");
  const [location,       setLocation]       = useState("South Africa");
  const [typeMode,       setTypeMode]       = useState(() => {
    const m = searchParams.get("mode");
    if (["all", "freelance", "permanent"].includes(m)) return m;
    return userProfile?.jobTypePreference || "freelance";
  });
  const [activeCategory, setActiveCategory] = useState(null);
  const [sortBy,         setSortBy]         = useState("newest");
  const [showSaved,      setShowSaved]      = useState(false);
  const didInit = useRef(false);

  // provinces[] from profile for client-side enforcement
  const userProvinces = useMemo(() => {
    if (userProfile?.provinces?.length) return userProfile.provinces;
    if (userProfile?.province) return [userProfile.province];
    return [];
  }, [userProfile]);

  // Province filter + sort + saved filter applied client-side after fetch
  const jobs = useMemo(() => {
    let filtered = filterByProvinces(rawJobs, userProvinces);
    if (showSaved) {
      filtered = filtered.filter(j => savedJobIds?.has(j.id));
    }
    if (sortBy === "oldest") {
      return [...filtered].sort((a, b) =>
        new Date(a.datePosted || 0) - new Date(b.datePosted || 0)
      );
    }
    return [...filtered].sort((a, b) =>
      new Date(b.datePosted || 0) - new Date(a.datePosted || 0)
    );
  }, [rawJobs, userProvinces, sortBy, showSaved, savedJobIds]);

  const debouncedSearch = useDebounce((kw, loc, mode) => {
    search({ keywords: kw, location: loc, keywordList: [], typeMode: mode });
  }, 500);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    const urlMode     = searchParams.get("mode");
    const urlQ        = searchParams.get("q") || "";
    const urlCategory = searchParams.get("category") || "";
    const mode        = ["all", "freelance", "permanent"].includes(urlMode)
      ? urlMode
      : (userProfile?.jobTypePreference || "freelance");
    setTypeMode(mode);

    // ?category=creative from email links — use proper keywords, not raw label text
    if (urlCategory) {
      const cat = CATEGORIES.find(c => c.id === urlCategory);
      if (cat) {
        setActiveCategory(cat.id);
        setQuery(cat.label);
        search({ keywords: cat.keyword, location: "South Africa", keywordList: [], typeMode: mode });
        return;
      }
    }

    // seed with user's top skills if no URL query — personalises first load
    const initKw = urlQ || (userProfile?.skills?.slice(0, 3).join(" ") || "");
    if (urlQ) setQuery(urlQ);

    // Apply preferred industry as active category on first load
    const industry = userProfile?.preferredIndustry;
    if (industry && !urlQ && !urlMode) {
      const cat = CATEGORIES.find(c => c.id === industry);
      if (cat) {
        setActiveCategory(cat.id);
        setQuery(cat.label);
        search({ keywords: cat.keyword, location: "South Africa", keywordList: [], typeMode: mode });
        return;
      }
    }

    search({ keywords: initKw, location: "South Africa", keywordList: [], typeMode: mode });
  }, [search, userProfile, searchParams]);

  const handleCategory = (cat) => {
    setActiveCategory(cat.id);
    setQuery(cat.label);
    search({ keywords: cat.keyword, location, keywordList: [], typeMode });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveCategory(null);
    search({ keywords: query, location, keywordList: [], typeMode });
  };

  const handleQueryChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    setActiveCategory(null);
    debouncedSearch(newQuery, location, typeMode);
  };

  // If the user hasn't typed anything, seed with their top skills so results are relevant
  const skillSeed = userProfile?.skills?.slice(0, 3).join(" ") || "";

  const handleTypeMode = (mode) => {
    setTypeMode(mode);
    const catKw = activeCategory
      ? CATEGORIES.find(c => c.id === activeCategory)?.keyword || query
      : query;
    const effectiveKw = catKw || skillSeed;
    search({ keywords: effectiveKw, location, keywordList: [], typeMode: mode });
  };

  const handleClear = () => {
    setActiveCategory(null);
    setQuery("");
    search({ keywords: skillSeed, location, keywordList: [], typeMode });
  };

  const activeCat = CATEGORIES.find(c => c.id === activeCategory);

  const profileIncomplete = userProfile && (
    (!userProfile?.provinces?.length && !userProfile?.province) ||
    !userProfile?.preferredIndustry
  );

  return (
    <AppLayout>
      <div className={styles.page}>
        {profileIncomplete && (
          <div className={styles.profileBanner}>
            <span>Set your <strong>province</strong> and <strong>industry</strong> to personalise your job feed.</span>
            <button className={styles.profileBannerBtn} onClick={() => navigate("/profile")}>
              Complete profile →
            </button>
          </div>
        )}
        <main className={styles.main}>
          {/* Hero */}
          <div className={styles.hero}>
            <h1 className={styles.heroTitle}>What work are you looking for?</h1>
            <p className={styles.heroSub}>Search freelance gigs across Mzansi</p>

            <form onSubmit={handleSearch} className={styles.searchBar}>
              <div className={styles.searchInputWrap}>
                <SearchIcon className={styles.searchIcon} size={16} strokeWidth={2} />
                <input className={styles.searchInput} value={query} onChange={handleQueryChange}
                  placeholder="e.g. React Developer, Graphic Designer, Plumber…" />
                {query && (
                  <button type="button" className={styles.searchClear}
                    onClick={() => { setQuery(""); setActiveCategory(null); debouncedSearch("", location, typeMode); }}>✕</button>
                )}
              </div>
              <div className={styles.locationWrap}>
                <MapPinIcon className={styles.locationIcon} size={14} strokeWidth={2} />
                <input className={styles.locationInput} value={location}
                  onChange={e => setLocation(e.target.value)}
                  placeholder={userProvinces.length ? userProvinces.join(", ") : "Province or city"} />
              </div>
              <Button type="submit" loading={loading}>Search</Button>
            </form>

            <div className={styles.typeRow}>
              {TYPE_MODES.map(({ id, label }) => (
                <button key={id} type="button"
                  className={`${styles.typeBtn} ${typeMode === id ? styles.typeBtnActive : ""}`}
                  onClick={() => handleTypeMode(id)}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Categories */}
          <section className={styles.categoriesSection}>
            <h2 className={styles.sectionTitle}>Browse by Industry</h2>
            <div className={styles.categoryGrid}>
              {CATEGORIES.map(cat => (
                <button key={cat.id}
                  className={`${styles.categoryTile} ${activeCategory === cat.id ? styles.categoryTileActive : ""}`}
                  onClick={() => handleCategory(cat)}>
                  <span className={styles.categoryIcon}><CategoryIcon id={cat.id} size={22} /></span>
                  <span className={styles.categoryLabel}>{cat.label}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Results */}
          <section className={styles.resultsSection}>
            <div className={styles.resultsBar}>
              {(activeCategory || query || userProvinces.length > 0) && !loading && (
                <span className={styles.resultsCount}>
                  {jobs.length} result{jobs.length !== 1 ? "s" : ""}
                  {activeCat ? ` in ${activeCat.label}` : query ? ` for "${query}"` : ""}
                  {` · ${typeMode === "all" ? "All types" : typeMode === "freelance" ? "Freelance" : "Permanent"}`}
                  {userProvinces.length > 0 && ` · ${userProvinces.join(", ")}`}
                </span>
              )}
              <div className={styles.filterRight}>
                <button
                  className={`${styles.savedBtn} ${showSaved ? styles.savedBtnActive : ""}`}
                  onClick={() => setShowSaved(v => !v)}
                  title="Show saved jobs"
                >
                  {showSaved ? "★ Saved" : "☆ Saved"}
                  {savedJobIds?.size > 0 && (
                    <span className={styles.savedCount}>{savedJobIds.size}</span>
                  )}
                </button>
                <div className={styles.sortToggle}>
                  <button
                    className={`${styles.sortBtn} ${sortBy === "newest" ? styles.sortBtnActive : ""}`}
                    onClick={() => setSortBy("newest")}
                  >Newest</button>
                  <button
                    className={`${styles.sortBtn} ${sortBy === "oldest" ? styles.sortBtnActive : ""}`}
                    onClick={() => setSortBy("oldest")}
                  >Oldest</button>
                </div>
                {(activeCategory || query) && !showSaved && (
                  <button className={styles.clearFilter} onClick={handleClear}>Clear ✕</button>
                )}
              </div>
            </div>

            {error && <div className={styles.errorBanner}>{error}</div>}

            {loading && jobs.length === 0 && <JobSearchLoader />}

            {!loading && jobs.length === 0 && !error && (
              <div className={styles.empty}>
                <SearchIcon size={40} strokeWidth={1.5} color="var(--color-text-muted)" />
                {userProvinces.length > 0 && rawJobs.length > 0 ? (
                  <>
                    <p>No jobs found in <strong>{userProvinces.join(", ")}</strong>.</p>
                    <button
                      className={styles.searchAllBtn}
                      onClick={() => search({ keywords: query, location: "South Africa", keywordList: [], typeMode })}
                    >
                      Search all of South Africa
                    </button>
                  </>
                ) : (
                  <p>No jobs found. Try a different search or browse a category above.</p>
                )}
              </div>
            )}

            {jobs.length > 0 && (
              <div className={styles.grid}>
                {jobs.map(job => <JobCard key={job.id} job={job} />)}
              </div>
            )}

            {loading && jobs.length > 0 && (
              <div className={styles.loadingMore}>
                <span className={styles.bounceDot} style={{ animationDelay: "0s" }} />
                <span className={styles.bounceDot} style={{ animationDelay: "0.15s" }} />
                <span className={styles.bounceDot} style={{ animationDelay: "0.3s" }} />
              </div>
            )}

            {!loading && hasMore && jobs.length > 0 && (
              <div className={styles.loadMoreRow}>
                <Button variant="secondary" onClick={loadMore}>Load more jobs</Button>
              </div>
            )}
          </section>
        </main>
      </div>
    </AppLayout>
  );
}
